// imports
import { useEffect, useState, useCallback, useMemo } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import cn from 'classnames'

// utils
import { AuthorisationStates, HeaderType } from '@/constants'
import { useNavigation } from '@/hooks/useNavigation'
import { TransitionScreenProvider, useTransitionScreen } from '@/contexts/transitionContext'
import { useAuthentication } from '@/hooks/useAuthentication'
import { useSpace } from '@/hooks/useSpace'
import useIdleLogout from '@/hooks/useIdleLogout'
import { RootState } from '@/utils/store/store'
import { setIsNavOpen, setDarkTheme } from '@/utils/store/features/application/applicationSlice'
import { SideNavContext } from '@/contexts/sideNavContext'
import type { SideNavigationMenuGroup } from '@/ui/components/Header/SideNavigation/types/SideNavigationTypes'
import type { LayoutContentProps } from './types/BaseLayoutTypes'

// components
import { Layout } from '@bosch/react-frok'
import Footer from '@/ui/components/Footer/Footer'
import Header from '@/ui/components/Header/Header'
import Modal from '@/ui/components/Modal/Modal'
import SideNavigation from '@/ui/components/Header/SideNavigation/SideNavigation'
import SideNavigationSkeleton from '@/ui/components/SkeletonLoading/SideNavigationSkeleton'
import { skeletonGroupsFromMenu, skeletonGroupsFromSpace } from '@/ui/components/SkeletonLoading/sideNavSkeletonItems'

// styles
import './BaseLayout.scss'
import storage from '@/utils/storage'
import ChatLoader from '@/ui/pages/Chat/ChatLoader'
import OnboardingPage from '@/ui/pages/Onboarding/OnboardingPage'

const LayoutContent = ({ baseLayoutClass, children }: LayoutContentProps) => {
    const location = useLocation()
    const { setShowTransitionScreen } = useTransitionScreen()

    useEffect(() => {
        setShowTransitionScreen(false)
    }, [location.pathname, setShowTransitionScreen])

    return (
        <Layout className={baseLayoutClass}>
            <div className='wrapper'>
                <div className='content'>
                    {children}
                </div>
            </div>
        </Layout>
    )
}

const BaseLayout = () => {
    const { darkTheme, isNavOpen } = useSelector((state: RootState) => state.application)
    const { authorisationFlow } = useSelector((state: RootState) => state.authentication)

    const [showOnboarding, setShowOnboarding] = useState(false)

    const { information, isAuthInitialized } = useAuthentication()

    useEffect(() => {
        const color = darkTheme ? '#000000' : '#ffffff'

        // Older Safari / Chrome / Android — meta tag
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color)

        // Safari 26+ dropped meta tag support; samples <body> / fixed header background instead
        document.body.style.backgroundColor = color
        document.documentElement.style.backgroundColor = color
    }, [darkTheme])

    const dispatch = useDispatch()
    const { navigateTo } = useNavigation()

    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key !== 'mma_user_information' || e.newValue === null) return
            try {
                const parsed = JSON.parse(e.newValue)
                dispatch(setDarkTheme(!!parsed.colorTheme))
            } catch { /* ignore malformed storage values */ }
        }

        window.addEventListener('storage', handleStorageChange)
        return () => window.removeEventListener('storage', handleStorageChange)
    }, [dispatch])
    const { switchingSpace, switchingSpaceId, spaces, isActivating } = useSpace()
    const { t, i18n } = useTranslation()
    const location = useLocation()
    useIdleLogout()

    const isHomePage = location.pathname === '/'

    // The lines are asked for per space: the onboarding is for the space the user is going to (the first part of the URL)
    const baseUrl = import.meta.env.BASE_URL ?? '/'
    const landingSegment = location.pathname.replace(baseUrl, '/').split('/').filter(Boolean)[0]
    const landingSpaceId = spaces.find(space => space.path === landingSegment || space.id === landingSegment)?.id
        // Startup can activate the default space while the router stays on `/`; in that case there is no URL segment.
        ?? (!landingSegment ? spaces.find(space => space.isActive)?.id : undefined)

    useEffect(() => {
        if (isAuthInitialized && information?.userId && landingSpaceId && !storage.getOnboardingCompleted(landingSpaceId)) {
            setShowOnboarding(true)
        }
    }, [isAuthInitialized, information?.userId, landingSpaceId])

    const [menuGroups, setMenuGroups] = useState<SideNavigationMenuGroup[] | null>(null)

    // The menu that the skeleton stands for: the one of the space that is switched to, else the one that is shown now
    const skeletonGroups = useMemo(() => {
        const targetSpace = spaces.find(space => space.id === switchingSpaceId)

        return targetSpace
            ? skeletonGroupsFromSpace(targetSpace, t, key => i18n.exists(key))
            : skeletonGroupsFromMenu(menuGroups)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spaces, switchingSpaceId, menuGroups, i18n.language])

    const headerType = HeaderType.MINIMAL
    const baseLayoutClass = cn('base-layout', {
        '--minimal-header': headerType === HeaderType.MINIMAL,
    })

    const toggleNav = useCallback((open?: boolean) => {
        dispatch(setIsNavOpen(open ?? !isNavOpen))
    }, [isNavOpen, dispatch])

    useEffect(() => {
        if (!isAuthInitialized) return

        const needLogin = authorisationFlow === AuthorisationStates.RELOGIN

        if (needLogin) {
            const basePath = import.meta.env.BASE_URL.replace(/\/$/, '')
            const strippedPathname = basePath
                ? window.location.pathname.replace(new RegExp(`^${basePath}`), '')
                : window.location.pathname
            const currentPath = `${strippedPathname}${window.location.search}`
            sessionStorage.removeItem('isAuthorizationInProgress')

            const isIdentityPath = currentPath.startsWith('/identity')
            // The link is one value of the login page: unencoded, the ? and & of a link with parameters (the MTS link)
            // cut it after the first one, and the line and the message are lost when the user comes back from the sign-in
            navigateTo(isIdentityPath ? '/identity/login' : `/identity/login?redirectUrl=${encodeURIComponent(currentPath)}`)
        }
        // The redirect runs when the authentication state changes, not when the navigation context renders again
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthInitialized, authorisationFlow])

    return (
        <SideNavContext.Provider value={{ menuGroups, setMenuGroups }}>
            <TransitionScreenProvider isDarkTheme={darkTheme}>
                <div className={cn('app', { 'is-home-page': isHomePage, [`-${darkTheme ? 'dark' : 'light'}-mode`]: true })}>
                    {showOnboarding && landingSpaceId && <OnboardingPage key={landingSpaceId} spaceId={landingSpaceId} onComplete={() => setShowOnboarding(false)} />}
                    <div className='base-layout-root'>
                        <Header headerType={headerType} />
                        <div className='base-body'>
                            <div className={cn('side-nav-wrapper', { '-expanded': isNavOpen })}>
                                {switchingSpace && menuGroups ? (
                                    <SideNavigationSkeleton isMenuOpen={isNavOpen ?? false} toggleMenu={toggleNav} groups={skeletonGroups} />
                                ) : (
                                    menuGroups && (
                                        <SideNavigation
                                            menuGroups={menuGroups}
                                            isMenuOpen={isNavOpen ?? false}
                                            toggleMenu={toggleNav}
                                        />
                                    )
                                )}
                            </div>
                                <div className='base-layout-wrapper'>
                                    <LayoutContent baseLayoutClass={baseLayoutClass}>
                                        {!showOnboarding && (isActivating && !isHomePage ? <ChatLoader /> : <Outlet />)}
                                    </LayoutContent>
                                    <Footer />
                                </div>
                        </div>
                    </div>
                    <Modal />
                </div>
            </TransitionScreenProvider>
        </SideNavContext.Provider>
    )
}

export default BaseLayout
